/**
 * The Tapes panel's four buttons, as behaviour: `LOAD NEW TAPE` (from a file),
 * `DELETE TAPE(S)`, export, and refreshing the list after any of them.
 *
 * Split from `routes/Vcr.tsx` for the same reason `vcr-controls.ts` is: the
 * awkward cases live here, and they are the ones worth testing — a file that is
 * not a tape, a shelf that is full, a browser with no storage at all.
 */

import { createSignal, type Accessor } from "solid-js";
import { parseTape, type ReplayTape } from "@red-opus/engine";
import { downloadTape } from "./tape-file.ts";
import {
  browserTapeStore,
  deleteTape,
  findTape,
  saveTape,
  tapeFileName,
  tapeSummaries,
  type TapeStore,
  type TapeSummary,
} from "./tape-store.ts";

export const NOT_A_TAPE_MESSAGE = "THAT FILE IS NOT A RED BARON TAPE.";
export const UNREADABLE_FILE_MESSAGE = "THAT FILE COULD NOT BE READ.";

/** A tape file, or `undefined` when it is not one. Import is the only place a stranger's bytes arrive. */
export function readTapeFile(text: string): ReplayTape | undefined {
  try {
    return parseTape(text);
  } catch {
    // `parseTape` has already refused to guess what the file meant, and the
    // caller tells the pilot it was not a tape — which is the whole of what can
    // usefully be said about somebody else's JSON.
    return undefined;
  }
}

export interface TapeShelfActions {
  readonly tapes: Accessor<readonly TapeSummary[]>;
  readonly error: Accessor<string | undefined>;
  readonly refresh: () => void;
  readonly remove: (name: string) => void;
  readonly exportTape: (name: string) => void;
  readonly importFile: (file: File) => void;
  readonly find: (name: string) => ReplayTape | undefined;
}

export function createTapeShelf(store: TapeStore | undefined = browserTapeStore()): TapeShelfActions {
  const [tapes, setTapes] = createSignal<readonly TapeSummary[]>(store ? tapeSummaries(store) : []);
  const [error, setError] = createSignal<string | undefined>(undefined);

  const refresh = (): void => {
    setTapes(store ? tapeSummaries(store) : []);
  };

  const shelve = (tape: ReplayTape): void => {
    if (!store) return;
    try {
      saveTape(store, tape);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
      return;
    }
    setError(undefined);
    refresh();
  };

  return {
    tapes,
    error,
    refresh,
    find: (name) => (store ? findTape(store, name) : undefined),
    remove: (name) => {
      if (store) deleteTape(store, name);
      refresh();
    },
    exportTape: (name) => {
      const found = store ? findTape(store, name) : undefined;
      if (found) downloadTape(found, tapeFileName(found));
    },
    importFile: (file) => {
      void file.text().then(
        (text) => {
          const parsed = readTapeFile(text);
          if (parsed) shelve(parsed);
          else setError(NOT_A_TAPE_MESSAGE);
        },
        () => setError(UNREADABLE_FILE_MESSAGE),
      );
    },
  };
}
